import loc from 'owa-localize';
import { OwaDate } from 'owa-datetime';
import { showInImmersiveReader } from 'owa-immersive-reader-store';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import { lazyMarkAsReadInTable } from 'owa-mail-mark-read-actions';
import { lazyMoveReadingPaneToTab } from 'owa-mail-reading-pane-store';
import { lazyOnReportAbuseOptionSelected } from 'owa-mail-report-abuse';
import * as lazyTriageActions from 'owa-mail-triage-action';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { CONTEXT_MENU_EMAIL_ENTITY_SUBTYPE } from 'owa-todo-types';
import { noSubject } from 'owa-locstrings/lib/strings/nosubject.locstring.json';
import { openMessageSourceDialog } from 'owa-mail-reading-pane-item-actions-view/lib/components/ViewMessageSourceDialog';
import { ClientItem, mailStore } from 'owa-mail-store';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { listViewStore, MailRowDataPropertyGetter, TableView } from 'owa-mail-list-store';
import { lazyShowConversationInImmersiveReader } from 'owa-mail-message-actions';
import { lazyFindEmailFromSender } from 'owa-mail-search';
import { lazyCreateRuleFromRow } from 'owa-mail-inbox-rules';
import { lazyOpenTasksView } from 'owa-time-panel';
import { logUsage } from 'owa-analytics';
import {
    onArchive,
    onReply,
    onReplyAll,
    onForward,
    onFlag,
    onUnflag,
    onSnooze,
} from 'owa-mail-commands';

const contextMenuActionSource = 'ContextMenu';
const contextListViewActionSource = 'ContextLV';

/**
 * Called when user clicks on the Snooze menu item
 * @param date An optional owaDate, where undefined means to unsnooze
 */
export function onSnoozeClicked(date?: OwaDate) {
    onSnooze(date, contextMenuActionSource);
}

/**
 * Called when user clicks on the reply menu item
 * @param rowKey the row key
 * @param tableViewId the table view id
 */
export function onReplyRow() {
    onReply(contextMenuActionSource);
}

/**
 * Called when user clicks on the reply all menu item
 * @param rowKey the row key
 * @param tableViewId the table view id
 */
export function onReplyAllRow() {
    onReplyAll(contextMenuActionSource);
}

/**
 * Called when user clicks on the forward menu item
 * @param rowKey the row key
 * @param tableViewId the table view id
 */
export function onForwardRow() {
    onForward(contextMenuActionSource);
}

/**
 * Called when user clicks on view message details or source
 * @param rowKey the row key
 * @param tableViewId the table view id
 */
export function onViewMessageDetails(rowKey: string, tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);

    const itemIds = MailRowDataPropertyGetter.getItemIds(rowKey, tableView);
    const itemForViewMessage: ClientItem = mailStore.items.get(itemIds[0]);
    logUsage('TnS_ViewMessageDetails', [contextMenuActionSource, isConsumer()]);
    openMessageSourceDialog(itemForViewMessage);
}

/**
 * Called when user clicks on the delete menu item
 */
export function onDeleteClicked(rowKeys: string[], tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    if (tableView.isInVirtualSelectAllMode) {
        // When in virtual selection, i.e non search and select all mode,
        // delete all in the folder
        lazyTriageActions.lazyEmptyTableView.importAndExecute(
            tableView,
            false /*isExplicitSoftDelete*/,
            contextListViewActionSource,
            tableView.virtualSelectAllExclusionList
        );
    } else {
        lazyTriageActions.lazyDeleteMailListRows.importAndExecute(
            rowKeys,
            tableViewId,
            false /*isExplicitSoftDelete*/,
            contextMenuActionSource
        );
    }
}

/**
 * Called when user clicks on the archive menu item
 */
export function onArchiveClicked() {
    onArchive(contextMenuActionSource);
}

/**
 * Called when the user clicks on the "Show in immersive reader" menu item
 */
export function onShowInImmersiveReaderClicked(rowKey: string, tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const rowId = MailRowDataPropertyGetter.getRowClientItemId(rowKey, tableView);

    switch (tableView.tableQuery.listViewType) {
        case ReactListViewType.Conversation:
            lazyShowConversationInImmersiveReader.importAndExecute(
                rowId /* conversationId */,
                contextMenuActionSource
            );
            break;

        case ReactListViewType.Message:
            showInImmersiveReader(rowId /* itemId */, contextMenuActionSource);
            break;
    }
}

/**
 * Called when the user clicks on the "Open in new tab" menu item
 */
export function onOpenInNewTabClicked(rowKeys: string[], tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const instrumentationContexts = getInstrumentationContextsFromTableView(rowKeys, tableView);
    for (let i = 0; i < rowKeys.length; i++) {
        const rowId = MailRowDataPropertyGetter.getRowIdToShowInReadingPane(rowKeys[i], tableView);
        const rowSubject = MailRowDataPropertyGetter.getSubject(rowKeys[i], tableView);
        const rowCategories = MailRowDataPropertyGetter.getCategories(rowKeys[i], tableView);
        lazyMoveReadingPaneToTab.importAndExecute(
            rowId,
            rowSubject,
            rowCategories,
            true /*makeActive*/,
            instrumentationContexts[i]
        );
    }
}

export function onMarkAsReadUnreadClicked(tableViewId: string, isReadValueSet: boolean) {
    const tableView = listViewStore.tableViews.get(tableViewId);

    lazyMarkAsReadInTable.importAndExecute(
        contextMenuActionSource,
        tableView.virtualSelectAllExclusionList /* exclusionList */,
        tableView.isInVirtualSelectAllMode /* isActingOnAllItemsInTable */,
        isReadValueSet,
        [...tableView.selectedRowKeys.keys()],
        tableView
    );
}

export async function onFindEmailFromSenderClicked(rowKey: string, tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const lastSenderMailbox = MailRowDataPropertyGetter.getLastSenderMailbox(rowKey, tableView);

    const findEmailFromSender = await lazyFindEmailFromSender.import();
    findEmailFromSender(lastSenderMailbox.Name, lastSenderMailbox.EmailAddress);
}

export function onBlockClicked(rowKeys: string[], tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    lazyTriageActions.lazyBlockLastSendersFromTable.importAndExecute(
        rowKeys,
        tableView,
        contextListViewActionSource
    );
}

export function markRowsAsFlagged() {
    onFlag(contextMenuActionSource);
}

export function markRowsAsNotFlagged() {
    onUnflag(contextMenuActionSource);
}

export function onJunkNotJunkClicked(
    rowKeys: string[],
    tableViewId: string,
    isJunkValueToSet: boolean
) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    lazyTriageActions.lazyMarkRowsJunkNotJunk.importAndExecute(
        rowKeys,
        tableView,
        isJunkValueToSet,
        contextMenuActionSource /* actionSource */
    );
}

export async function onIgnoreStopIgnoreClicked(
    rowKeys: string[],
    tableView: TableView,
    shouldIgnore: boolean
) {
    const toggleIgnoreConversations = await lazyTriageActions.lazyToggleIgnoreConversations.import();
    toggleIgnoreConversations(rowKeys, tableView, shouldIgnore, contextListViewActionSource);
}

export function markRowsPinnedUnpinned(
    rowKeys: string[],
    tableViewId: string,
    shouldMarkAsPinned: boolean
) {
    lazyTriageActions.lazyMarkRowsPinnedUnpinned.importAndExecute(
        rowKeys,
        tableViewId,
        shouldMarkAsPinned,
        contextMenuActionSource
    );
}

/**
 * Called when user clicks on the create rule menu item
 */
export async function onCreateRuleClicked(rowKey: string, tableViewId: string) {
    const createRuleFromRow = await lazyCreateRuleFromRow.import();
    createRuleFromRow(rowKey, tableViewId, contextListViewActionSource);
}

/**
 * Called when the user clicks on the "Translate" menu item
 */
export function onTranslateClicked(rowKeys: string[], tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    lazyTriageActions.lazyTranslateFromTable.importAndExecute(
        rowKeys,
        tableView,
        contextListViewActionSource
    );
}

/**
 * Called when the user clicks on the "Report Abuse" menu item
 */
export function onReportAbuseClicked(rowKey: string, tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const instrumentationContexts = getInstrumentationContextsFromTableView([rowKey], tableView);
    lazyOnReportAbuseOptionSelected.importAndExecute(
        rowKey,
        tableView,
        instrumentationContexts,
        contextMenuActionSource
    );
}

/**
 * Opens the Time Panel to Tasks view, and creates a task from the email
 */
export function openTasksView(rowKey: string, tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const itemIds = MailRowDataPropertyGetter.getItemIds(rowKey, tableView);
    let subject = MailRowDataPropertyGetter.getSubject(rowKey, tableView);
    if (!subject) {
        subject = loc(noSubject);
    }
    const createTaskActionPayload = {
        initializePayload: {},
        actionPayload: {
            type: 'switchListAndCreateTodos' as const,
            todos: [
                {
                    Subject: subject,
                    LinkedEmail: {
                        Id: itemIds[0],
                        EntitySubtype: CONTEXT_MENU_EMAIL_ENTITY_SUBTYPE,
                        DisplayName: subject,
                    },
                },
            ],
        },
    };
    lazyOpenTasksView.importAndExecute('CreateTaskInContextMenu', createTaskActionPayload);
}

export function onMarkAsPhishingClicked(rowKeys: string[], tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);

    lazyTriageActions.lazyMarkRowsAsPhishingFromTable.importAndExecute(
        rowKeys,
        tableView,
        'ContextMenu'
    );
}
